import { useEffect, useState } from 'react';
import {
  AlertCircle,
  Check,
  Download,
  Eye,
  FileText,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  DOCUMENT_TYPES,
  CONFIDENTIALITE_LEVELS,
  type DocumentType,
  type ConfidentialiteLevel,
  type DocumentEntityType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Liste des documents attachés à une entité (US-BAT-06).
 *
 *  - Upload nouveau document (multipart + métadonnées JSON)
 *  - Liste + filtre catégorie + niveau de confidentialité
 *  - Téléchargement via URL signée (TTL 5 min)
 *  - Suppression soft delete
 */

interface DocumentRow {
  id: string;
  entity_type: string;
  entity_id: string;
  nom: string;
  type_document: DocumentType;
  taille: number | null;
  format: string | null;
  mime_type: string | null;
  version: number;
  date_validite_debut: string | null;
  date_validite_fin: string | null;
  niveau_confidentialite: ConfidentialiteLevel | null;
  statut_validation: string | null;
  created_at: string;
}

export function DocumentsList({
  entityType,
  entityId,
}: {
  entityType: DocumentEntityType;
  entityId: string;
}) {
  const [items, setItems] = useState<DocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [previewing, setPreviewing] = useState<DocumentRow | null>(null);

  useEffect(() => {
    refresh();
  }, [entityType, entityId]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      params.set('entity_type', entityType);
      params.set('entity_id', entityId);
      params.set('page_size', '100');
      const res = await api.get<{ data: DocumentRow[] }>(`/documents?${params}`);
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleDownload(doc: DocumentRow) {
    try {
      const res = await api.get<{ data: { signed_url: string } }>(`/documents/${doc.id}/download`);
      window.open(res.data.data.signed_url, '_blank');
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  async function handleDelete(doc: DocumentRow) {
    if (!confirm(`Supprimer le document "${doc.nom}" ?`)) return;
    try {
      await api.delete(`/documents/${doc.id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-2 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">{items.length} document(s)</p>
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-primary-200 text-primary-700 bg-white rounded-lg hover:bg-primary-50"
        >
          <Upload className="w-3.5 h-3.5 mr-1" />
          Ajouter un document
        </button>
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucun document attaché.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((doc) => (
            <li
              key={doc.id}
              className="flex items-start justify-between rounded-lg border border-gray-200 p-2 hover:bg-gray-50"
            >
              <div className="flex items-start space-x-2 min-w-0 flex-1">
                <FileText className="w-4 h-4 text-primary-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {doc.nom}
                    {doc.version > 1 && (
                      <span className="ml-1.5 text-xs text-gray-400">v{doc.version}</span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {doc.type_document}
                    {doc.format && ` · ${doc.format.toUpperCase()}`}
                    {doc.taille && ` · ${formatBytes(doc.taille)}`}
                    {doc.niveau_confidentialite && doc.niveau_confidentialite !== 'interne' && (
                      <span
                        className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${confColor(
                          doc.niveau_confidentialite,
                        )}`}
                      >
                        {doc.niveau_confidentialite}
                      </span>
                    )}
                  </p>
                  {doc.date_validite_fin && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Validité jusqu'au {new Date(doc.date_validite_fin).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                {isPreviewable(doc) && (
                  <button
                    onClick={() => setPreviewing(doc)}
                    className="p-1.5 rounded text-blue-600 hover:bg-blue-50"
                    title="Prévisualiser"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-1.5 rounded text-primary-600 hover:bg-primary-50"
                  title="Télécharger"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(doc)}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {uploadOpen && (
        <DocumentUploadModal
          entityType={entityType}
          entityId={entityId}
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            refresh();
          }}
        />
      )}
      {previewing && <DocumentPreviewModal doc={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

/**
 * Détermine si un document est prévisualisable inline.
 * - PDF : `<iframe sandbox>` (le navigateur prend en charge le rendu)
 * - Images : `<img>` direct (jpeg / png / webp / gif)
 * - Tout le reste : téléchargement obligatoire (cf. US-TR-12).
 */
function isPreviewable(doc: DocumentRow): boolean {
  if (!doc.mime_type) return false;
  return doc.mime_type === 'application/pdf' || doc.mime_type.startsWith('image/');
}

function DocumentPreviewModal({ doc, onClose }: { doc: DocumentRow; onClose(): void }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get<{ data: { signed_url: string } }>(
          `/documents/${doc.id}/download`,
        );
        setSignedUrl(res.data.data.signed_url);
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      }
    })();
  }, [doc.id]);

  const isPdf = doc.mime_type === 'application/pdf';
  const isImage = doc.mime_type?.startsWith('image/');

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl w-full max-w-5xl shadow-2xl max-h-[95vh] flex flex-col">
          <div className="flex items-center justify-between p-3 border-b border-gray-200 flex-shrink-0">
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold text-gray-900 truncate">{doc.nom}</h3>
              <p className="text-xs text-gray-500">
                {doc.type_document} · {doc.format?.toUpperCase()} · v{doc.version}
              </p>
            </div>
            <div className="flex items-center space-x-2 ml-2 flex-shrink-0">
              {signedUrl && (
                <a
                  href={signedUrl}
                  download
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Télécharger
                </a>
              )}
              <button
                onClick={onClose}
                className="p-1 rounded-lg hover:bg-gray-100"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-gray-100">
            {errorMessage ? (
              <div className="p-6 text-center">
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 inline-flex items-start space-x-2 text-left max-w-md">
                  <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
              </div>
            ) : !signedUrl ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : isPdf ? (
              <iframe
                src={signedUrl}
                title={doc.nom}
                className="w-full h-[80vh] bg-white"
                // `allow-scripts` est requis : le viewer PDF interne de Chrome
                // refuse de s'afficher sans (depuis ~Chrome 110). On omet
                // `allow-same-origin` pour que d'eventuels scripts embarques
                // dans le PDF restent isoles du DOM parent.
                sandbox="allow-scripts"
              />
            ) : isImage ? (
              <div className="flex items-center justify-center min-h-[60vh] p-4">
                <img
                  src={signedUrl}
                  alt={doc.nom}
                  className="max-w-full max-h-[80vh] object-contain bg-white shadow-sm"
                />
              </div>
            ) : (
              <div className="p-6 text-center text-sm text-gray-500">
                Ce format ne supporte pas la prévisualisation inline. Utilisez le bouton
                "Télécharger" pour ouvrir le fichier.
              </div>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload modal
// ─────────────────────────────────────────────────────────────────────────────

function DocumentUploadModal({
  entityType,
  entityId,
  onClose,
  onUploaded,
}: {
  entityType: DocumentEntityType;
  entityId: string;
  onClose(): void;
  onUploaded(): void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [nom, setNom] = useState('');
  const [typeDoc, setTypeDoc] = useState<DocumentType>('plan_architectural');
  const [conf, setConf] = useState<ConfidentialiteLevel>('interne');
  const [validiteDebut, setValiditeDebut] = useState('');
  const [validiteFin, setValiditeFin] = useState('');
  const [uploading, setUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleUpload(e: React.FormEvent) {
    e.preventDefault();
    if (!file) {
      setErrorMessage('Sélectionnez un fichier.');
      return;
    }
    setUploading(true);
    setErrorMessage(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append(
        'metadata',
        JSON.stringify({
          entity_type: entityType,
          entity_id: entityId,
          nom: nom || file.name,
          type_document: typeDoc,
          niveau_confidentialite: conf,
          date_validite_debut: validiteDebut || null,
          date_validite_fin: validiteFin || null,
        }),
      );
      await api.post('/documents', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Ajouter un document</h3>
            <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <form onSubmit={handleUpload} className="p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-2 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fichier *</label>
              <input
                type="file"
                required
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="block w-full text-sm text-gray-700 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
              {file && (
                <p className="text-xs text-gray-500 mt-1">
                  {file.name} · {formatBytes(file.size)}
                </p>
              )}
            </div>
            <Field label="Nom (par défaut = nom du fichier)">
              <input
                value={nom}
                onChange={(e) => setNom(e.target.value)}
                placeholder={file?.name ?? ''}
                className={inputClass}
              />
            </Field>
            <Field label="Type de document *">
              <select
                value={typeDoc}
                onChange={(e) => setTypeDoc(e.target.value as DocumentType)}
                className={inputClass}
              >
                {DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Confidentialité">
              <select
                value={conf}
                onChange={(e) => setConf(e.target.value as ConfidentialiteLevel)}
                className={inputClass}
              >
                {CONFIDENTIALITE_LEVELS.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Validité début">
                <input
                  type="date"
                  value={validiteDebut}
                  onChange={(e) => setValiditeDebut(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Validité fin">
                <input
                  type="date"
                  value={validiteFin}
                  onChange={(e) => setValiditeFin(e.target.value)}
                  className={inputClass}
                />
              </Field>
            </div>
            <div className="flex justify-end space-x-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={uploading || !file}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Uploader
              </button>
            </div>
          </form>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}

function confColor(level: ConfidentialiteLevel): string {
  switch (level) {
    case 'public':
      return 'bg-green-100 text-green-700';
    case 'interne':
      return 'bg-blue-100 text-blue-700';
    case 'restreint':
      return 'bg-orange-100 text-orange-700';
    case 'confidentiel':
      return 'bg-red-100 text-red-700';
  }
}
